import { Eye, EyeOff } from "lucide-react";
import { usePostHog } from "@posthog/react";
import { Auth } from "utils/cognito-auth";
import { useIdentityContext } from "contexts/identity-context";
import { Form, Formik, FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import { useIntercom } from "react-use-intercom";
import { object, ref, string } from "yup";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldError } from "@/components/ui/field";

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
}

interface Values {
  currentPassword: string;
  newPassword: string;
  verifyNewPassword: string;
}

const ChangePasswordModal = ({ open, onClose }: ChangePasswordModalProps) => {
  const [showPasswordCurrent, setShowPasswordCurrent] = useState(false);
  const [showPasswordNew, setShowPasswordNew] = useState(false);
  const [showPasswordVerify, setShowPasswordVerify] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const { signOut } = useIdentityContext();
  const { shutdown } = useIntercom();
  const posthog = usePostHog();
  const [closedown, setClosedown] = useState(5);

  const validationSchema = object().shape({
    currentPassword: string().required("Current password is required"),
    newPassword: string()
      .min(8, "Password must be at least 8 characters")
      .required("New password is required"),
    verifyNewPassword: string()
      .oneOf([ref("newPassword")], "Passwords must match")
      .required("Confirm your new password"),
  });

  useEffect(() => {
    if (closedown > 0) {
      const timer = setTimeout(() => {
        setClosedown((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (closedown === 0 && successMsg) {
      const logoutGlobally = async () => {
        posthog.capture("user_signed_out");
        shutdown();
        await signOut({ global: true });
      };
      logoutGlobally();
      handleClose();
    }
  }, [closedown, successMsg]);

  const handleFormSubmit = async (
    values: Values,
    actions: FormikHelpers<Values>,
  ) => {
    setErrorMsg("");
    setSuccessMsg("");
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(
        user,
        values.currentPassword,
        values.newPassword,
      ).then(() => {
        setClosedown(5);
      });
      setSuccessMsg("Password changed successfully.");
      actions.resetForm();
    } catch (err: any) {
      setErrorMsg(err.message || "An error occurred");
    } finally {
      actions.setSubmitting(false);
    }
  };

  const handleClose = () => {
    setErrorMsg("");
    setSuccessMsg("");
    setShowPasswordCurrent(false);
    setShowPasswordNew(false);
    setShowPasswordVerify(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !successMsg) handleClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>
            Changing your password will invalidate all logged in sessions.
          </DialogDescription>
        </DialogHeader>
        <Formik
          initialValues={{
            currentPassword: "",
            newPassword: "",
            verifyNewPassword: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
            isValid,
            dirty,
          }) => (
            <Form className="space-y-4">
              <Field>
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    name="currentPassword"
                    type={showPasswordCurrent ? "text" : "password"}
                    value={values.currentPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={
                      touched.currentPassword && Boolean(errors.currentPassword)
                    }
                    className="pr-10"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2"
                    onClick={() =>
                      setShowPasswordCurrent(!showPasswordCurrent)
                    }
                  >
                    {showPasswordCurrent ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                </div>
                {touched.currentPassword && errors.currentPassword && (
                  <FieldError>{errors.currentPassword}</FieldError>
                )}
              </Field>

              <Field>
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type={showPasswordNew ? "text" : "password"}
                    value={values.newPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={
                      touched.newPassword && Boolean(errors.newPassword)
                    }
                    className="pr-10"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2"
                    onClick={() => setShowPasswordNew(!showPasswordNew)}
                  >
                    {showPasswordNew ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                </div>
                {touched.newPassword && errors.newPassword && (
                  <FieldError>{errors.newPassword}</FieldError>
                )}
              </Field>

              <Field>
                <Label htmlFor="verifyNewPassword">Verify New Password</Label>
                <div className="relative">
                  <Input
                    id="verifyNewPassword"
                    name="verifyNewPassword"
                    type={showPasswordVerify ? "text" : "password"}
                    value={values.verifyNewPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    aria-invalid={
                      touched.verifyNewPassword &&
                      Boolean(errors.verifyNewPassword)
                    }
                    className="pr-10"
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2"
                    onClick={() =>
                      setShowPasswordVerify(!showPasswordVerify)
                    }
                  >
                    {showPasswordVerify ? (
                      <EyeOff className="size-4" />
                    ) : (
                      <Eye className="size-4" />
                    )}
                  </button>
                </div>
                {touched.verifyNewPassword && errors.verifyNewPassword && (
                  <FieldError>{errors.verifyNewPassword}</FieldError>
                )}
              </Field>

              {errorMsg && (
                <Alert variant="destructive">
                  <AlertDescription>{errorMsg}</AlertDescription>
                </Alert>
              )}
              {successMsg && (
                <Alert variant="success">
                  <AlertDescription>
                    {`${successMsg} You will be logged out in ${closedown} seconds.`}
                  </AlertDescription>
                </Alert>
              )}

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting || !!successMsg}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || !!successMsg || !isValid || !dirty}
                >
                  Change Password
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePasswordModal;
